import { StandardEvents } from '@shopify/events';

class MistaraCatalog extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    this.controller = new AbortController();
    this.cartFeedback = new Map();
    this.view = 'grid';
    try {
      if (sessionStorage.getItem('mistara-catalog-view') === 'list') this.view = 'list';
    } catch {}

    this.addEventListener('click', (event) => {
      const button = event.target.closest('[data-catalog-view]');
      if (!button || !this.contains(button)) return;
      this.view = button.dataset.catalogView === 'list' ? 'list' : 'grid';
      try {
        sessionStorage.setItem('mistara-catalog-view', this.view);
      } catch {}
      this.applyView();
    }, { signal: this.controller.signal });

    this.addEventListener('change', (event) => {
      const select = event.target;
      if (!(select instanceof HTMLSelectElement) || !select.matches('[data-catalog-sort]')) return;
      const facets = select.form?.closest('facets-form-component');
      if (typeof facets?.updateFilters === 'function') {
        facets.updateFilters();
      } else {
        select.form?.requestSubmit();
      }
    }, { signal: this.controller.signal });

    this.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
      const form = event.target.closest?.('.mistara-catalog__purchase product-form-component');
      const button = form?.querySelector('[data-catalog-cart-button]');
      if (!button || event.action !== 'add' || !event.promise) return;
      this.showCartFeedback(form, button, event.promise);
    }, { signal: this.controller.signal });

    this.addEventListener(StandardEvents.cartError, (event) => {
      const purchase = event.target.closest?.('.mistara-catalog__purchase');
      const message = purchase?.querySelector('[data-catalog-cart-error]');
      if (!message) return;
      message.textContent = event.detail?.description || event.error || message.dataset.defaultError;
      message.hidden = false;
    }, { signal: this.controller.signal });

    this.observer = new MutationObserver(() => {
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        this.applyView();
      });
    });
    this.observer.observe(this, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['product-grid-view', 'data-catalog-view']
    });
    this.applyView();
  }

  disconnectedCallback() {
    this.controller?.abort();
    this.observer?.disconnect();
    if (this.frame) cancelAnimationFrame(this.frame);
    this.frame = null;
    for (const [button, feedback] of this.cartFeedback ?? []) {
      clearTimeout(feedback.timer);
      button.disabled = feedback.wasDisabled;
      button.removeAttribute('aria-busy');
      delete button.dataset.catalogCartState;
    }
    this.cartFeedback?.clear();
  }

  async showCartFeedback(form, button, promise) {
    const previous = this.cartFeedback.get(button);
    if (previous) clearTimeout(previous.timer);
    const feedback = { wasDisabled: previous?.wasDisabled ?? button.disabled };
    this.cartFeedback.set(button, feedback);
    const message = form.closest('.mistara-catalog__purchase').querySelector('[data-catalog-cart-error]');
    if (message) {
      message.hidden = true;
      message.textContent = '';
    }
    button.disabled = true;
    button.setAttribute('aria-busy', 'true');
    button.dataset.catalogCartState = 'loading';

    try {
      const result = await promise;
      if (this.cartFeedback.get(button) !== feedback) return;
      if (result?.detail?.didError || result?.userErrors?.length) {
        this.resetCartFeedback(button, feedback);
        if (message?.hidden) {
          message.textContent = result.userErrors?.[0]?.message || message.dataset.defaultError;
          message.hidden = false;
        }
        return;
      }
      button.removeAttribute('aria-busy');
      button.dataset.catalogCartState = 'success';
      feedback.timer = setTimeout(() => this.resetCartFeedback(button, feedback), 1600);
    } catch {
      if (this.cartFeedback.get(button) !== feedback) return;
      this.resetCartFeedback(button, feedback);
      if (message?.hidden) {
        message.textContent = message.dataset.defaultError;
        message.hidden = false;
      }
    }
  }

  resetCartFeedback(button, feedback) {
    if (this.cartFeedback.get(button) !== feedback) return;
    button.disabled = feedback.wasDisabled;
    button.removeAttribute('aria-busy');
    delete button.dataset.catalogCartState;
    this.cartFeedback.delete(button);
  }

  applyView() {
    if (this.dataset.catalogView !== this.view) this.dataset.catalogView = this.view;
    this.querySelectorAll('[ref="grid"]').forEach((grid) => {
      if (grid.getAttribute('product-grid-view') !== 'default') grid.setAttribute('product-grid-view', 'default');
    });
    this.querySelectorAll('button[data-catalog-view]').forEach((button) => {
      const pressed = String(button.dataset.catalogView === this.view);
      if (button.getAttribute('aria-pressed') !== pressed) button.setAttribute('aria-pressed', pressed);
    });
  }
}

if (!customElements.get('mistara-catalog')) customElements.define('mistara-catalog', MistaraCatalog);
